import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { toastConfig } from 'assets/data/config';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  DGPTAlertConfig,
  DGPTAlertTrigger,
  DGPTAlertCIR,
} from 'types/dgptAlerts';

export const keys = {
  alertConfigs: 'alertConfigs',
} as const;

export interface CreateAlertConfigPayload {
  name: string;
  eventType: string;
  refreshCadence: string;
  aorId?: string;
  triggers?: DGPTAlertTrigger[];
  cirs?: DGPTAlertCIR[];
  active?: boolean;
  archived?: boolean;
}

export interface UpdateAlertConfigPayload
  extends Partial<CreateAlertConfigPayload> {
  id: string;
}

// Fetch alert configs
export const useAlertConfigs = () => {
  const groupGuid = useSelector(selectGroupGuid);

  return useQuery<DGPTAlertConfig[], Error>({
    queryKey: ['alertConfigs', groupGuid],
    queryFn: async () => {
      if (!groupGuid) return [];

      try {
        const response = await axios.get(
          `${config.apiGateway.dgptAlerts}/alert-configs`,
          {
            params: { groupGuid },
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching alert configs:', error);
        toast.error('Failed to fetch alert configs.', toastConfig);
        throw error;
      }
    },
    enabled: !!groupGuid,
  });
};

// Fetch a single alert config by ID
export const useAlertConfigById = (configId: string) => {
  const queryOptions: UseQueryOptions<DGPTAlertConfig, Error> = {
    queryKey: [keys.alertConfigs, configId],
    queryFn: async () => {
      const response = await axios.get(
        `${config.apiGateway.dgptAlerts}/alert-configs/${configId}`
      );
      return response.data;
    },
    enabled: !!configId,
  };

  return useQuery<DGPTAlertConfig, Error>(queryOptions);
};

// Create an alert config
export const useCreateAlertConfig = () => {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);

  return useMutation<DGPTAlertConfig, Error, CreateAlertConfigPayload>({
    mutationFn: async (data) => {
      const response = await axios.post(
        `${config.apiGateway.dgptAlerts}/alert-configs`,
        {
          ...data,
          groupGuid,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertConfigs', groupGuid] });
    },
    onError: (error) => {
      console.error('Error creating alert config:', error);
      toast.error('Failed to create alert config.', toastConfig);
    },
  });
};

// Update an alert config
export const useUpdateAlertConfig = () => {
  const queryClient = useQueryClient();

  return useMutation<DGPTAlertConfig, Error, UpdateAlertConfigPayload>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      const response = await axios.put(
        `${config.apiGateway.dgptAlerts}/alert-configs/${id}`,
        rest
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.alertConfigs] });
    },
    onError: (error) => {
      console.error('Error updating alert config:', error);
      toast.error('Failed to update alert config.', toastConfig);
    },
  });
};

// Delete an alert config
export const useDeleteAlertConfig = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const response = await axios.delete(
        `${config.apiGateway.dgptAlerts}/alert-configs/${id}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.alertConfigs] });
      toast.success('Alert configuration deleted successfully.', toastConfig);
    },
    onError: (error) => {
      console.error('Error deleting alert config:', error);
      toast.error('Failed to delete alert config.', toastConfig);
    },
  });
};
